import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChatText, MapPin, Trash, CheckSquare, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Shell } from "@/components/Shell";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

export default function Inbox() {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // {mode:'single'|'bulk', id?, count}
  const [busy, setBusy] = useState(false);

  const load = async () => setItems((await api.get("/messages")).data);
  useEffect(() => { load(); }, []);

  const locale = lang === "de" ? "de-AT" : "en-GB";
  const fmtSeen = (iso) => new Date(iso).toLocaleString(locale, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  const open = async (m) => {
    if (m.read) return;
    try { await api.post(`/messages/${m.id}/read`); } catch { /* noop */ }
    setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, read: true, read_at: x.read_at || new Date().toISOString() } : x)));
  };

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const onCardClick = (m) => { if (selectMode) toggleSel(m.id); else open(m); };

  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };

  const doDelete = async () => {
    setBusy(true);
    try {
      if (confirm.mode === "bulk") {
        const r = await api.post("/messages/delete", { ids: [...selected] });
        toast.success(`${r.data.deleted} Nachricht(en) gelöscht`);
      } else {
        await api.post(`/messages/${confirm.id}/delete`);
        toast.success("Nachricht gelöscht");
      }
      setConfirm(null);
      exitSelect();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Shell showNav={false}>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between gap-2">
          <h1 className="text-2xl font-heading font-semibold text-gray-900 flex items-center gap-2"><ChatText size={26} weight="duotone" />{t("inboxTitle")}</h1>
          {items.length > 0 && (
            selectMode ? (
              <button data-testid="inbox-select-cancel" onClick={exitSelect} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1"><X size={16} weight="bold" />Abbrechen</button>
            ) : (
              <button data-testid="inbox-select-toggle" onClick={() => setSelectMode(true)} className="sb-btn px-3 py-2 bg-white text-sm font-medium flex items-center gap-1"><CheckSquare size={16} weight="bold" />Auswählen</button>
            )
          )}
        </div>

        {selectMode && (
          <div className="mt-3 flex items-center justify-between sb-card p-3" data-testid="inbox-bulk-bar">
            <span className="font-body text-sm text-gray-700">{selected.size} ausgewählt</span>
            <button data-testid="inbox-bulk-delete" disabled={selected.size === 0} onClick={() => setConfirm({ mode: "bulk", count: selected.size })}
              className="sb-btn px-3 py-2 text-white text-sm font-medium flex items-center gap-1 disabled:opacity-40" style={{ background: "#EF4444" }}>
              <Trash size={16} weight="bold" />Löschen
            </button>
          </div>
        )}

        {items.length === 0 ? (
          <p className="font-body text-gray-500 mt-5">{t("noMessagesInbox")}</p>
        ) : (
          <div className="space-y-3 mt-5">
            {items.map((m) => (
              <div key={m.id} data-testid="inbox-item"
                className="sb-card p-4 w-full text-left relative flex gap-3"
                style={!m.read ? { borderColor: "var(--app-primary)", boxShadow: "4px 4px 0 0 var(--app-primary)" } : {}}>
                {selectMode && (
                  <input type="checkbox" data-testid={`inbox-check-${m.id}`} checked={selected.has(m.id)} onChange={() => toggleSel(m.id)}
                    className="h-5 w-5 mt-1 shrink-0 accent-[var(--app-primary)]" />
                )}
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onCardClick(m)}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-heading font-semibold text-gray-900 text-sm flex items-center gap-2 min-w-0">
                      {!m.read && <span data-testid="inbox-unread-dot" className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: "var(--app-primary)" }} />}
                      <span className="truncate">{m.tag_label || t("unnamed")} <span className="text-gray-400 font-body">/t/{m.tag_code}</span></span>
                    </p>
                    <p className="text-xs font-body text-gray-400 shrink-0">{new Date(m.timestamp).toLocaleString(locale)}</p>
                  </div>
                  <p className="font-body text-gray-700 mt-1">"{m.message}"</p>
                  <p className="text-xs font-body text-gray-500 mt-0.5">— {m.finder_name || "Anonym"}</p>
                  {m.found_location && <p className="text-xs font-body text-gray-600 mt-0.5">{t("foundLocation")}: {m.found_location}</p>}
                  {m.finder_phone && <a href={`tel:${m.finder_phone}`} onClick={(e) => e.stopPropagation()} className="text-xs font-body underline mt-0.5 inline-block" style={{ color: "var(--app-strong)" }}>{m.finder_phone}</a>}
                  {m.lat != null && m.lng != null && (
                    <a href={`https://www.google.com/maps?q=${m.lat},${m.lng}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      className="text-xs font-heading font-medium underline flex items-center gap-1 mt-1 w-max" style={{ color: "var(--app-strong)" }}>
                      <MapPin size={13} weight="fill" />{t("viewLocation")}
                    </a>
                  )}
                  <p data-testid={`inbox-seen-${m.id}`} className="text-[11px] font-body mt-1.5" style={{ color: m.read_at ? "#6B7280" : "var(--app-primary)" }}>
                    {m.read_at ? `Gesehen: ${fmtSeen(m.read_at)}` : "Ungelesen"}
                  </p>
                </div>
                {!selectMode && (
                  <button data-testid={`inbox-delete-${m.id}`} onClick={() => setConfirm({ mode: "single", id: m.id })}
                    className="shrink-0 self-start h-8 w-8 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50 transition-colors" title="Löschen">
                    <Trash size={18} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="inbox-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Nachricht löschen?</DialogTitle></DialogHeader>
          <p className="font-body text-sm text-gray-600">
            {confirm?.mode === "bulk"
              ? `Möchtest du ${confirm?.count} Nachricht(en) wirklich löschen? Das kann nicht rückgängig gemacht werden.`
              : "Möchtest du diese Nachricht wirklich löschen? Das kann nicht rückgängig gemacht werden."}
          </p>
          <div className="flex gap-2 mt-4">
            <button data-testid="inbox-confirm-cancel" onClick={() => setConfirm(null)} className="sb-btn flex-1 py-3 bg-white font-medium">Abbrechen</button>
            <button data-testid="inbox-confirm-delete" disabled={busy} onClick={doDelete} className="sb-btn flex-1 py-3 text-white font-medium disabled:opacity-50" style={{ background: "#EF4444" }}>{busy ? "…" : "Löschen"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
